import { API_BASE_URL } from '../config/environment';
import type { AuthSession, AuthUser } from '../types/auth';

interface AuthErrorResponse {
  error?: unknown;
  message?: unknown;
}

export class AuthApiService {
  async signInWithGoogle(credential: string): Promise<AuthSession> {
    const response = await fetch(`${API_BASE_URL}/auth/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ credential }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch((): AuthErrorResponse => ({}));
      const message = getErrorMessage(errorData, response.status);
      throw new Error(message);
    }

    const data: unknown = await response.json();
    if (!isAuthSession(data)) {
      throw new Error('Invalid auth response');
    }
    return data;
  }
}

function getErrorMessage(errorData: AuthErrorResponse, status: number): string {
  if (typeof errorData.error === 'string') return errorData.error;
  if (typeof errorData.message === 'string') return errorData.message;
  return `Google sign-in failed (${status})`;
}

function isAuthSession(value: unknown): value is AuthSession {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return isAuthUser(record.user)
    && typeof record.sessionToken === 'string'
    && typeof record.expiresAt === 'string';
}

function isAuthUser(value: unknown): value is AuthUser {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return typeof record.id === 'string'
    && typeof record.name === 'string'
    && (record.role === 'admin' || record.role === 'user')
    && (record.email === undefined || typeof record.email === 'string')
    && (record.pictureUrl === undefined || typeof record.pictureUrl === 'string');
}

export const authApi = new AuthApiService();
